import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';

import { LoggerConfig, LogTransport, LogLevel } from '../interfaces/logger.interface';
import { ConsoleTransport } from '../transports/console.transport';
import { HttpTransport } from '../transports/http.transport';
import { LocalStorageTransport } from '../transports/local-storage.transport';
import { developmentLoggerConfig, qaLoggerConfig, productionLoggerConfig } from '../config/logger.config';

export interface LoggingModuleConfig {
  environment: 'development' | 'qa' | 'production';
  enableConsole?: boolean;
  enableHttp?: boolean;
  enableLocalStorage?: boolean;
  httpEndpoint?: string;
  enableInterceptor?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LoggerFactoryService {

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Optional() private http?: HttpClient
  ) {}

  createLoggerConfig(moduleConfig: LoggingModuleConfig): LoggerConfig {
    const transports: LogTransport[] = [];

    // Always add console transport (works in both SSR and browser)
    if (moduleConfig.enableConsole) {
      const consoleTransport = new ConsoleTransport(this.platformId);
      consoleTransport.minLevel = LogLevel.DEBUG;
      transports.push(consoleTransport);
    }

    // Only add browser-specific transports in browser environment
    if (isPlatformBrowser(this.platformId)) {
      if (moduleConfig.enableHttp && moduleConfig.httpEndpoint && this.http) {
        const httpTransport = new HttpTransport(this.http, this.platformId);
        httpTransport.setEndpoint(moduleConfig.httpEndpoint);
        // WARN+ only — INFO breadcrumbs stay in the browser console.
        // Prevents the server-side rate limiter from blocking the app
        // during normal use and keeps centralized logs focused on actionable signal.
        httpTransport.minLevel = LogLevel.WARN;
        transports.push(httpTransport);
      }

      if (moduleConfig.enableLocalStorage) {
        const localStorageTransport = new LocalStorageTransport(this.platformId);
        localStorageTransport.minLevel = LogLevel.WARN;
        transports.push(localStorageTransport);
      }
    }

    // Get base config based on environment
    let baseConfig: Partial<LoggerConfig>;
    switch (moduleConfig.environment) {
      case 'production':
        baseConfig = productionLoggerConfig;
        break;
      case 'qa':
        baseConfig = qaLoggerConfig;
        break;
      default:
        baseConfig = developmentLoggerConfig;
    }

    return {
      minLevel: baseConfig.minLevel || LogLevel.DEBUG,
      transports,
      enableStackTrace: baseConfig.enableStackTrace || false,
      enableSourceMapping: baseConfig.enableSourceMapping || false,
      maxRetries: baseConfig.maxRetries || 3,
      bufferSize: baseConfig.bufferSize || 1,
      flushInterval: baseConfig.flushInterval || 0
    };
  }
}
